import { useState } from 'react';
import { Power, PowerOff, Edit, Trash2 } from 'lucide-react';
import { IconDotsVertical } from '@tabler/icons-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/hooks/useAuth';
import type { Offer } from '../../types';
import { useUpdateOfferStatus, useDeleteOffer } from '../../hooks/useOfferManagement';

interface OfferActionsProps {
  offer: Offer;
  onEdit?: (offer: Offer) => void;
}

export function OfferActions({
  offer,
  onEdit,
}: OfferActionsProps) {
  const { hasPermission } = useAuth();
  const updateStatus = useUpdateOfferStatus();
  const deleteOffer = useDeleteOffer();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleStatusToggle = () => {
    updateStatus.mutate({
      id: offer.id,
      is_active: !offer.is_active,
    });
  };

  const handleDelete = () => {
    deleteOffer.mutate(offer.id);
    setShowDeleteDialog(false);
  };

  // Check if user has any relevant permissions
  const canUpdate = hasPermission('offer_management:update');
  const canDelete = hasPermission('offer_management:delete');

  // Don't render anything if user has no permissions
  if (!canUpdate && !canDelete) {
    return null;
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="data-[state=open]:bg-muted text-muted-foreground flex size-8 ml-auto"
            size="icon"
            data-testid={`offer-actions-${offer.id}`}
            disabled={updateStatus.isPending || deleteOffer.isPending}
          >
            <IconDotsVertical />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-40">
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {canUpdate && onEdit && (
            <DropdownMenuItem
              onClick={() => onEdit(offer)}
              data-testid={`edit-offer-${offer.id}`}
            >
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
          )}
          
          {canUpdate && (
            <DropdownMenuItem
              onClick={handleStatusToggle}
              disabled={updateStatus.isPending}
              data-testid={`toggle-offer-status-${offer.id}`}
            >
              {offer.is_active ? (
                <>
                  <PowerOff className="mr-2 h-4 w-4" />
                  Deactivate
                </>
              ) : (
                <>
                  <Power className="mr-2 h-4 w-4" />
                  Activate
                </>
              )}
            </DropdownMenuItem>
          )}
          
          {canDelete && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setShowDeleteDialog(true)}
                className="text-destructive focus:text-destructive"
                data-testid={`delete-offer-${offer.id}`}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="delete-offer-dialog">
          <DialogHeader>
            <DialogTitle>Delete Offer</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this offer? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <p className="text-sm font-medium">{offer.short_description}</p>
            <p className="text-sm text-muted-foreground">
              From {offer.venue_name}
            </p>
            {offer.total_claims > 0 && (
              <p className="text-sm text-amber-600">
                Warning: This offer has {offer.total_claims} claim(s)
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleteOffer.isPending}
              data-testid="cancel-delete-offer"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteOffer.isPending}
              data-testid="confirm-delete-offer"
            >
              {deleteOffer.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}